import React, { useRef } from "react";
import { motion } from "framer-motion";
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Heart,
  Globe,
  Award,
  Headphones,
} from "lucide-react";
import "./HoneymoonTestimonials.css";

const TESTIMONIALS_DATA = [
  {
    id: 1,
    coupleName: "Sophia & Liam Davis",
    destination: "Bora Bora, French Polynesia",
    avatar:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80",
    rating: 5,
    review:
      "Our overwater bungalow experience was pure magic! Everything was seamlessly planned down to our private sunset dinner on the beach.",
    travelDate: "October 2025",
  },
  {
    id: 2,
    coupleName: "Ethan & Isabella Chen",
    destination: "Santorini, Greece",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
    rating: 5,
    review:
      "Waking up to the caldera views each morning was a dream. The local winery tours and catamaran cruise were highlights of our life.",
    travelDate: "June 2025",
  },
  {
    id: 3,
    coupleName: "Marcus & Olivia Taylor",
    destination: "Kyoto & Tokyo, Japan",
    avatar:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80",
    rating: 5,
    review:
      "From traditional ryokans in Kyoto to luxury high-rises in Tokyo, the contrast was incredible. Truly a once-in-a-lifetime honeymoon.",
    travelDate: "April 2025",
  },
  {
    id: 4,
    coupleName: "Lucas & Ava Rodriguez",
    destination: "Amalfi Coast, Italy",
    avatar:
      "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=400&q=80",
    rating: 5,
    review:
      "The cliffside hotel views took our breath away! Every recommendation made by the team was spot-on. We couldn't have asked for more.",
    travelDate: "August 2025",
  },
];

const TRUST_STATS = [
  { id: 1, icon: Heart, label: "10,000+ Happy Couples" },
  { id: 2, icon: Globe, label: "50+ Destinations" },
  { id: 3, icon: Award, label: "4.9/5 Average Rating" },
  { id: 4, icon: Headphones, label: "24/7 Support" },
];

const HoneymoonTestimonials = () => {
  const trackRef = useRef(null);

  const handleScroll = (direction) => {
    if (trackRef.current) {
      const amount = direction === "left" ? -400 : 400;
      trackRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  return (
    <section className="hmt-section">
      <span className="hmt-orb hmt-orb-a" aria-hidden="true" />
      <span className="hmt-orb hmt-orb-b" aria-hidden="true" />

      <div className="hmt-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="hmt-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="hmt-header-text">
            <h2 className="hmt-title">Love Stories from Our Travelers</h2>
            <p className="hmt-subtitle">
              Real couples. Unforgettable journeys. Beautiful memories.
            </p>
          </div>

          <div className="hmt-controls">
            <button
              type="button"
              className="hmt-ctrl"
              onClick={() => handleScroll("left")}
              aria-label="Previous testimonials"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="hmt-ctrl"
              onClick={() => handleScroll("right")}
              aria-label="Next testimonials"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.header>

        {/* ---------- Rail ---------- */}
        <div className="hmt-rail" ref={trackRef}>
          {TESTIMONIALS_DATA.map((item, index) => {
            const isFeatured = index === 0;
            return (
              <motion.article
                key={item.id}
                className={`hmt-card ${isFeatured ? "is-featured" : ""}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.5,
                  delay: Math.min(index, 6) * 0.08,
                }}
              >
                <Quote className="hmt-quote-bg" size={140} aria-hidden="true" />

                {isFeatured && (
                  <span className="hmt-featured-badge">Featured Story</span>
                )}

                <div className="hmt-body">
                  <div
                    className="hmt-stars"
                    aria-label={`${item.rating} out of 5 stars`}
                  >
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <Star
                        key={i}
                        className="hmt-star"
                        size={14}
                        fill="currentColor"
                        aria-hidden="true"
                      />
                    ))}
                  </div>

                  <p className="hmt-review">“{item.review}”</p>

                  <div className="hmt-profile">
                    <img
                      src={item.avatar}
                      alt={item.coupleName}
                      className="hmt-avatar"
                      loading="lazy"
                    />
                    <div className="hmt-profile-info">
                      <h3 className="hmt-name">{item.coupleName}</h3>
                      <p className="hmt-dest">{item.destination}</p>
                      <span className="hmt-date">{item.travelDate}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* ---------- Trust stats ---------- */}
        <motion.div
          className="hmt-stats"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {TRUST_STATS.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.id} className="hmt-stat">
                <span className="hmt-stat-icon">
                  <Icon size={16} aria-hidden="true" />
                </span>
                <span className="hmt-stat-label">{stat.label}</span>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default HoneymoonTestimonials;